/**
 * preorder-variant.js
 * Reads per-variant preorder metafields (preorder.is_preorder, arrival_date.preorder_ship_date)
 * and updates the product page buy button text and arrival date message on variant change.
 *
 * Hooks into Canopy's `on:variant:change` event — no theme files modified.
 */

(function () {
  'use strict';

  var S = window.__preorderStrings || {};
  var PREORDER_BTN_TEXT  = S.preorder      || 'Pre-order';
  var ADD_TO_CART_TEXT   = S.add_to_cart   || 'Add to cart';
  var ARRIVAL_MESSAGE    = S.arrival_message || 'This item is expected to arrive {{date}} and will ship within 2–3 business days after.';
  var PAYMENT_NOTE       = S.payment_note  || 'Payment is collected at checkout. Your order ships once the item arrives.';
  var ARRIVAL_TBC        = S.arrival_tbc   || 'This item is available for pre-order. Arrival date to be confirmed. Payment is collected at checkout.';
  var ARRIVAL_THIS_WEEK  = S.arrival_this_week || 'This item is expected to arrive this week and will ship within 2–3 business days after.';

  // The metafield holds the warehouse ETA. Customer-facing dates are padded so a
  // shipment landing a day or two late still reads as on time. Applied to both the
  // on-page message and the cart line-item property so they always agree.
  var ARRIVAL_BUFFER_DAYS = 3;

  function addDays(isoString, days) {
    var d = new Date(isoString + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  }

  /** The date a customer should be shown for a given warehouse ETA. */
  function displayDate(isoString) {
    return formatDate(addDays(isoString, ARRIVAL_BUFFER_DAYS));
  }

  /**
   * True once the warehouse ETA (not the padded display date) is today or earlier.
   * The shipment is then imminent-or-late; a specific date would either be today or
   * already wrong, so the message falls back to "this week" until the engine's
   * grace period deactivates the preorder or the ETA is updated.
   */
  function etaHasPassed(isoString) {
    var today = new Date().toISOString().slice(0, 10);
    return isoString <= today;
  }

  /**
   * Format an ISO date string (YYYY-MM-DD) using the page locale.
   * Produces unambiguous long-form: "August 24, 2026".
   */
  function formatDate(isoString) {
    if (!isoString) return '';
    try {
      var lang = document.documentElement.lang || 'en';
      return new Intl.DateTimeFormat(lang, {
        year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
      }).format(new Date(isoString + 'T00:00:00Z'));
    } catch (_) {
      // Fallback for environments without Intl support
      var parts = isoString.split('-');
      return parts[1] + '/' + parts[2] + '/' + parts[0];
    }
  }

  function loadVariantData() {
    var el = document.getElementById('preorder-variant-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (_) { return null; }
  }

  /** Scopes to .js-product-form-main to avoid hitting quickbuy forms. */
  function findForm() {
    return document.querySelector('.js-product-form-main');
  }

  /**
   * Add or remove the "Preorder Arrival" line item property on the form.
   * Shopify reads hidden inputs named properties[*] on form submission,
   * so this attaches the arrival date to the cart line without any fetch interception.
   * The property appears in the cart, checkout, order confirmation, and admin order view.
   */
  function setLineItemProperty(form, dateString) {
    var PROP_NAME = 'properties[Preorder Arrival]';
    var existing = form.querySelector('input[name="' + PROP_NAME + '"]');
    if (dateString) {
      if (!existing) {
        existing = document.createElement('input');
        existing.type = 'hidden';
        existing.name = PROP_NAME;
        form.appendChild(existing);
      }
      existing.value = dateString;
    } else {
      if (existing) existing.parentNode.removeChild(existing);
    }
  }

  function findButton() {
    var form = findForm();
    return form ? form.querySelector('button[name="add"]') : null;
  }

  /**
   * The message element is rendered server-side in the Liquid section with aria-live="polite"
   * so screen readers register the live region before JS fires.
   * JS only populates text content — never replaces the element.
   */
  function getMessageEl() {
    return document.getElementById('preorder-arrival-message');
  }

  /**
   * Mark the currently-selected swatch(es) with .is-preorder-swatch so merchants
   * can style the indicator via the theme's CSS.
   */
  function markActiveSwatch(form, isPreorder) {
    if (!form) return;
    form.querySelectorAll('.is-preorder-swatch').forEach(function (el) {
      el.classList.remove('is-preorder-swatch');
    });
    if (!isPreorder) return;
    form.querySelectorAll('input[type="radio"]:checked').forEach(function (input) {
      var label = form.querySelector('label[for="' + input.id + '"]') || input.parentElement;
      if (label) label.classList.add('is-preorder-swatch');
    });
  }

  function applyVariant(variantId, variantData, form) {
    if (!variantData) return;

    var data       = variantData[String(variantId)];
    var isPreorder = data && (data.is_preorder === true || data.is_preorder === 'true');
    var shipDate   = data && data.ship_date || null;

    // ── Button text ──
    var btn = findButton();
    if (btn && !btn.disabled) {
      var newText = isPreorder ? PREORDER_BTN_TEXT : ADD_TO_CART_TEXT;
      // HCF: the theme's hcf-atc-label snippet renders the button as "<verb> — N units"
      // and re-applies one tick after every variant change, composing from
      // data-hcf-verb. Hand it the verb so "Pre-order — 500 units" survives its
      // re-render instead of being overwritten back to "Add to cart — 500 units".
      // (This script runs deferred, i.e. before DOMContentLoaded, so on first paint
      // the label's own apply() already sees the right verb.)
      btn.dataset.hcfVerb = newText;
      btn.textContent = newText;
      // Wrap in setTimeout(0) so our write lands after any synchronous Canopy
      // handlers that also read dataset.addToCartText in the same event cycle.
      setTimeout(function () { btn.dataset.addToCartText = newText; }, 0);
    }

    // ── Arrival message (aria-live region — announced automatically by screen readers) ──
    var msgEl = getMessageEl();
    if (msgEl) {
      if (isPreorder && shipDate) {
        var line1 = etaHasPassed(shipDate)
          ? ARRIVAL_THIS_WEEK
          : ARRIVAL_MESSAGE.replace('{{date}}', displayDate(shipDate));
        msgEl.textContent = line1 + ' ' + PAYMENT_NOTE;
        msgEl.style.display = '';
      } else if (isPreorder) {
        // is_preorder true but no date set — show TBC rather than silent failure
        msgEl.textContent = ARRIVAL_TBC;
        msgEl.style.display = '';
      } else {
        msgEl.textContent = '';
        msgEl.style.display = 'none';
      }
    }

    // ── Line item property (shows in cart, checkout, admin, order emails) ──
    var activeForm = form || findForm();
    if (activeForm) {
      setLineItemProperty(activeForm, isPreorder && shipDate ? displayDate(shipDate) : null);
    }

    // ── Swatch marking ──
    markActiveSwatch(activeForm, isPreorder);
  }

  function init() {
    var variantData = loadVariantData();
    if (!variantData) return;

    var form = findForm();
    if (!form) {
      console.warn('[preorder-variant] .js-product-form-main not found — preorder state will not apply.');
      return;
    }

    // Move the aria-live message element adjacent to the add-to-cart button so
    // the visual connection between button and note is clear.
    var msgEl = getMessageEl();
    var btn = findButton();
    if (msgEl && btn) {
      var container = btn.closest('.product-info__add-to-cart')
        || btn.closest('.product-info__add-button')
        || btn.parentElement;
      if (container && container !== msgEl.parentElement) {
        container.insertAdjacentElement('afterend', msgEl);
      }
    }

    // Apply initial state from the currently selected variant
    var hiddenInput = form.querySelector('input[name="id"]');
    if (hiddenInput) {
      applyVariant(hiddenInput.value, variantData, form);
    } else {
      console.warn('[preorder-variant] Hidden variant input not found — initial state skipped.');
    }

    // Update on variant change
    document.addEventListener('on:variant:change', function (event) {
      var variant = event.detail && event.detail.variant;
      var eventForm = event.detail && event.detail.form;
      if (variant) applyVariant(variant.id, variantData, eventForm);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
